import {
	AfterViewInit,
	Component,
	Input,
	OnDestroy,
	OnChanges,
	ViewChild,
	OnInit,
} from '@angular/core';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import {
	DataSetComponent,
	DropdownOption,
} from 'src/app/components/data-set/data-set.component';
import {
	ApiLoadingKeys,
	ApiService,
	QueryParam,
} from 'src/app/services/api.services';
import { ControlAssignment } from 'src/app/models/data/deviceControl';
import { Subscription } from 'rxjs';
import { FeatureFlagName } from 'src/app/models/data/featureFlag';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { AssignDeviceControlsComponent } from 'src/app/ui/shared/assign-device-controls/assign-device-controls.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
	Device,
	DeviceAccessSource,
	hasOnlyGen3WallMountDevices,
	hasOnlyPdqDevices,
	hasOnlySpecialGrantAccessDevices,
	hasSentriVaultDevices,
} from 'src/app/models/data/device';
import { AccessDatePipe } from 'src/app/components/pipes';
import { DateTime } from 'luxon';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import {
	LoadingConfirmationDialogComponent,
	LoadingConfirmationDialogComponentData,
} from 'src/app/components/loading-confirmation-dialog/loading-confirmation-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetToolbarComponent,
	DataSetSearchActionGroupDirective,
	DataSetPrimaryActionGroupDirective,
	DataSetActionButtonComponent,
	DataSetSelectedActionGroupDirective,
	DataSetContentComponent,
} from '../../../components/data-set/data-set.component';
import { DataSetSearchComponent } from '../../../components/data-set/data-set-search.component';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatSelect, MatOption } from '@angular/material/select';
import {
	DataSetTableComponent,
	DataSetTableRowSelectionComponent,
	DataSetTableMenuComponent,
} from '../../../components/data-set/data-set-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatCheckbox } from '@angular/material/checkbox';
import { HasSystemCapabilityDirective } from '../../../directives/system-capabilities/has-system-capability.directive';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatMenu, MatMenuContent, MatMenuItem } from '@angular/material/menu';
import {
	DeviceControlDaysPipe,
	DeviceControlDaysOnlyPipe,
	DeviceControlHoursOnlyPipe,
	ControlAssignmentDurationPipe,
} from '../../../components/pipes';
import { DataSetLoadingRowComponent } from '../../../components/data-set/data-set-loading-row.component';
import {
	MatDatepicker,
	MatDatepickerInput,
	MatDatepickerToggle,
} from '@angular/material/datepicker';
import { MatSuffix } from '@angular/material/form-field';
import { formatDate } from '@angular/common';

@Component({
	selector: 'app-company-access',
	templateUrl: './company-access.component.html',
	styleUrls: [
		'./company-access.component.scss',
		'../../../ui/common/main.scss',
	],
	providers: [AccessDatePipe],
	imports: [
		DataSetComponent_1,
		DataSetToolbarComponent,
		DataSetSearchActionGroupDirective,
		DataSetSearchComponent,
		MatFormField,
		MatLabel,
		MatInput,
		FormsModule,
		MatSelect,
		MatOption,
		MatDatepickerInput,
		MatDatepickerToggle,
		MatSuffix,
		MatDatepicker,
		DataSetPrimaryActionGroupDirective,
		DataSetActionButtonComponent,
		DataSetSelectedActionGroupDirective,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatCheckbox,
		MatCellDef,
		MatCell,
		DataSetTableRowSelectionComponent,
		HasSystemCapabilityDirective,
		MatSortHeader,
		MatIcon,
		MatTooltip,
		DataSetTableMenuComponent,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		MatMenu,
		MatMenuContent,
		MatMenuItem,
		DeviceControlDaysPipe,
		DeviceControlDaysOnlyPipe,
		DeviceControlHoursOnlyPipe,
		ControlAssignmentDurationPipe,
		DataSetLoadingRowComponent,
	],
})
export class CompanyAccessTabComponent
	implements AfterViewInit, OnDestroy, OnChanges, OnInit
{
	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;
	@Input() devices: {
		device: Device;
		groupNames?: string[];
		source?: DeviceAccessSource;
	}[] = [];
	@Input() companyId?: string;
	@Input() deviceGroupId?: string;
	@Input() deviceId?: string;
	@Input() hasAjaxDevice?: boolean;
	@Input() hasOnlyPDQ?: boolean;
	@Input() hasSentriVaultDevice?: boolean;

	localizeInherited: string = $localize`:@@companyAccessTab-inherited:Inherited`;
	localizeTo: string = $localize`:@@companyAccessTab-to:To`;
	localizeNone: string = $localize`:@@companyAccessTab-none:None`;
	expiredText: string = $localize`:@@companyAccessTab-expiredDuration:Expired`;

	displayedColumns = [
		'rowSelect',
		'companyName',
		'controlName',
		'deviceGroupName',
		'subAuthorize',
		'allowCodeGeneration',
		'duration',
		'source',
		'rowMenu',
	];
	/** PDQ-only views omit Allow Code Generation (code gen is shown via Control icons). */
	displayedColumnsPDQOnly = [
		'rowSelect',
		'companyName',
		'controlName',
		'deviceGroupName',
		'subAuthorize',
		'duration',
		'source',
		'rowMenu',
	];
	dataSource = new MatTableDataSource<ControlAssignment>();

	/** Columns for the current device context (hides allowCodeGeneration for PDQ-only). */
	get visibleColumns(): string[] {
		return this.hasOnlyPDQDevices
			? this.displayedColumnsPDQOnly
			: this.displayedColumns;
	}

	subscription?: Subscription;
	private selectionSubscription?: Subscription;
	private paginationSubscription?: Subscription;

	hasDeviceManagementSystemCapability: boolean;
	hasUserRoleManagement: boolean;
	hasOwnedDevices = false;

	deviceAccessTooltip = $localize`:@@companyAccess-deviceAccessTooltip:Device Access`;
	installDeviceTooltip = $localize`:@@companyAccess-installDeviceTooltip:Install Device`;
	removeDeviceTooltip = $localize`:@@companyAccess-removeDeviceTooltip:Remove Device`;
	accessCodeTooltip = $localize`:@@companyAccess-accessCodeTooltip:Generate Access Code`;
	haspCodeTooltip = $localize`:@@companyAccess-haspCodeTooltip:Generate Hasp Code`;
	armDisarmAlarmTooltip = $localize`:@@companyAccess-armDisarmAlarmTooltip:Arm/Disarm Alarm`;
	alarmOverrideTooltip = $localize`:@@companyAccess-alarmOverrideTooltip:Alarm Override`;

	filterLabels = {
		defaultPlaceholder: $localize`:@@companyAccess-defaultPlaceholder:Search company name...`,
		companyName: $localize`:@@companyAccess-filterOptionLabelCompanyName:Company Name`,
		controlName: $localize`:@@companyAccess-filterOptionLabelControl:Control`,
		deviceGroupName: $localize`:@@companyAccess-filterOptionLabelDeviceGroup:Device Group`,
		subAuthorize: $localize`:@@companyAccess-filterOptionLabelSubAuthorize:Sub-Authorize`,
		allowCodeGeneration: $localize`:@@companyAccess-filterOptionLabelAllowCodeGen:Allow Code Generation`,
		duration: $localize`:@@companyAccess-filterOptionLabelDuration:Duration`,
		source: $localize`:@@companyAccess-filterOptionLabelSource:Source`,
		activeDate: $localize`:@@companyAccess-filterOptionLabelActiveDate:Active Date`,
	};

	yesNoOptions: DropdownOption[] = [
		{ label: $localize`:@@companyAccess-filterOptionYes:Yes`, value: '1' },
		{ label: $localize`:@@companyAccess-filterOptionNo:No`, value: '0' },
	];

	isHaspCodeGenFeatureEnabled: boolean = false;

	getControlAssignmentsLoadingKey = ApiLoadingKeys.getControlAssignments;
	finishedLoadingInitData: boolean = false;

	/** True for a PDQ device detail page, or a device group that contains only PDQ devices. */
	hasOnlyPDQDevices: boolean = false;
	/** True for a SentriVault device / SentriVault-only group (does not hide Allow Code Generation). */
	hasSentriVaultDevices: boolean = false;
	/** True for Senturian/PDQ-only, SentriVault-only, or mixed of only those types. */
	hasOnlySpecialGrantAccessDevices: boolean = false;
	/** True when every device is a Gen3 wall mount (hides Generate Hasp Code). */
	hasOnlyGen3WallMountDevices: boolean = false;

	// Reactive checkbox state properties
	isAllSelected: boolean = false;
	isIndeterminate: boolean = false;

	constructor(
		private readonly apiService: ApiService,
		private readonly scService: SystemCapabilityService,
		private readonly dialog: MatDialog,
		private readonly accessDatePipe: AccessDatePipe,
		private readonly snackbar: MatSnackBar,
		public readonly loadingService: LoadingIndicatorService
	) {
		this.hasDeviceManagementSystemCapability = scService.has(
			SystemCapabilityEnum.deviceManagement
		);
		this.hasUserRoleManagement = this.scService.has(
			SystemCapabilityEnum.userRoleManagement
		);
	}

	async ngOnInit(): Promise<void> {
		this.updateDeviceTypeFlags();

		this.isHaspCodeGenFeatureEnabled = (
			await this.apiService.getFeatureFlagByName(FeatureFlagName.webHaspCodes)
		)?.value;
	}

	get editAccessLabel(): string {
		return $localize`:@@companyAccess-editAccess:Edit Access`;
	}

	ngAfterViewInit(): void {
		this.dataSet?.applyFilters();
		this.subscription = this.dataSet?.queryParams.subscribe(async (filters) => {
			const nextFilters = filters.map((filter): QueryParam =>
				filter.value instanceof Date
					? {
							...filter,
							value: formatDate(filter.value, 'yyyy-MM-dd', 'en-US'),
						}
					: filter
			);
			if (this.deviceGroupId) {
				nextFilters.push({ param: 'deviceGroupId', value: this.deviceGroupId });
			} else if (this.deviceId) {
				nextFilters.push({ param: 'deviceIds', value: [this.deviceId] });
				nextFilters.push({ param: 'deviceStatus', value: 'active' });
			}
			const result = await this.apiService.getControlAssignments([
				...nextFilters,
				{ param: 'type', value: 'company' },
			]);
			this.finishedLoadingInitData = true;
			this.dataSource.data = result;
			if (!this.dataSet.selection.isEmpty()) {
				const selectedIds = this.dataSet.selection.selected.map((it) => it.id);
				const selectedItems = result.filter((it) =>
					selectedIds.includes(it.id)
				);
				this.dataSet.clearAllSelections();
				this.dataSet.selection.select(...selectedItems);
			}
			this.updateCheckboxState();
		});

		// Subscribe to selection changes to update checkbox state reactively
		this.selectionSubscription = this.dataSet.selectedChanged.subscribe(() => {
			this.updateCheckboxState();
		});

		// Subscribe to pagination changes to update checkbox state when switching pages
		this.paginationSubscription =
			this.dataSet.presentationFilterChanged.subscribe(() => {
				this.updateCheckboxState();
			});
	}

	ngOnDestroy(): void {
		this.subscription?.unsubscribe();
		this.subscription = undefined;

		this.selectionSubscription?.unsubscribe();
		this.selectionSubscription = undefined;

		this.paginationSubscription?.unsubscribe();
		this.paginationSubscription = undefined;
	}

	ngOnChanges(): void {
		const ownedDevices = this.devices.filter(
			(device) => device.source?.companyId === this.companyId
		);
		this.hasOwnedDevices = ownedDevices.length > 0;

		this.updateDeviceTypeFlags();
	}

	private updateDeviceTypeFlags(): void {
		// Prefer backend/parent hasOnlyPDQ (PDQ-only groups) over local derivation
		if (typeof this.hasOnlyPDQ === 'boolean') {
			this.hasOnlyPDQDevices = this.hasOnlyPDQ;
		} else {
			this.hasOnlyPDQDevices = hasOnlyPdqDevices(this.devices);
		}

		if (typeof this.hasSentriVaultDevice === 'boolean') {
			this.hasSentriVaultDevices = this.hasSentriVaultDevice;
		} else {
			this.hasSentriVaultDevices = hasSentriVaultDevices(this.devices);
		}

		this.hasOnlySpecialGrantAccessDevices = hasOnlySpecialGrantAccessDevices(
			this.devices ?? []
		);

		this.hasOnlyGen3WallMountDevices = hasOnlyGen3WallMountDevices(
			this.devices ?? []
		);
	}

	formatControlName(control: ControlAssignment): string {
		return control.isSystemAssignment
			? this.localizeInherited
			: (control.deviceControl?.name ?? '');
	}

	isActive(control: ControlAssignment): boolean {
		const startDate = DateTime.fromISO(control.utcStartDate, { zone: 'UTC' });
		const endDate = DateTime.fromISO(
			control.utcEndDate ?? '2500-12-31T23:59:59.999Z',
			{ zone: 'UTC' }
		);
		const currentDate = DateTime.utc();

		return startDate <= currentDate && endDate > currentDate;
	}

	onGrantAccessClicked(): void {
		const data = {
			deviceGroupId: this.deviceGroupId,
			deviceId: this.deviceId,
			assignmentType: 'company',
			devices: this.devices,
			companyId: this.companyId,
			hasAjaxDevice: this.hasAjaxDevice,
			hasOnlyPDQ: this.hasOnlyPDQ,
			hasSentriVaultDevices: this.hasSentriVaultDevices,
			hasOnlySpecialGrantAccessDevices: this.hasOnlySpecialGrantAccessDevices,
		};
		this.dialog
			.open(AssignDeviceControlsComponent, {
				data,
				panelClass: 'drawer-dialog',
			})
			.afterClosed()
			.subscribe((_: any) => this.dataSet.applyFilters());
	}

	onDeleteControlAssignmentsClicked(): void {
		const controlAssignmentIds = this.dataSet.selection.selected.map(
			(it: ControlAssignment) => it.id
		);
		this.deleteControlAssignments(controlAssignmentIds);
	}

	onEditControlAssignmentsClicked(
		controlAssignments?: ControlAssignment[]
	): void {
		const data = {
			deviceGroupId: this.deviceGroupId,
			deviceId: this.deviceId,
			assignmentType: 'company',
			devices: JSON.parse(JSON.stringify(this.devices)),
			companyId: this.companyId,
			hasAjaxDevice: this.hasAjaxDevice,
			hasOnlyPDQ: this.hasOnlyPDQ,
			hasSentriVaultDevices: this.hasSentriVaultDevices,
			hasOnlySpecialGrantAccessDevices: this.hasOnlySpecialGrantAccessDevices,
			// Edit mode specific data
			isEditMode: true,
			controlAssignments: JSON.parse(
				JSON.stringify(controlAssignments || this.dataSet.selection.selected)
			),
			showCompanyMessage: true,
		};
		this.dialog
			.open(AssignDeviceControlsComponent, {
				data,
				panelClass: 'drawer-dialog',
			})
			.afterClosed()
			.subscribe((result: any) => {
				if (!!result) {
					this.dataSet.applyFilters();
				}
			});
	}

	onRowMenuDeleteClicked(controlAssignment: ControlAssignment): void {
		this.deleteControlAssignments([controlAssignment.id]);
	}

	onRowMenuEditClicked(controlAssignment: ControlAssignment): void {
		this.onEditControlAssignmentsClicked([controlAssignment]);
	}

	deleteControlAssignments(controlAssignmentIds: string[]): void {
		const data: LoadingConfirmationDialogComponentData = {
			apiFunctionToExecute: () =>
				this.apiService.deleteControlAssignments(controlAssignmentIds),
			loadingKeyOfApiFunction: ApiLoadingKeys.deleteControlAssignments,

			headerIcon: 'sl-danger-circle',
			headerTextBeforeLoading: $localize`:@@companyAccessTab-confirm:Confirm`,
			bodyTextBeforeLoading: $localize`:@@companyAccessTab-removalConfirmationText:This action will remove access for both the connected device and any sub-authorized permissions. This cannot be restored. Are you sure?`,
			negativeButtonDialogText: $localize`:@@companyAccessTab-cancel:Cancel`,
			positiveButtonDialogText: $localize`:@@companyAccessTab-remove:Remove`,

			negativeButtonClass: 'secondary-small-button',
			positiveButtonClass: 'warning-small-button',
		};

		const dialogRef: MatDialogRef<LoadingConfirmationDialogComponent> =
			this.dialog.open(LoadingConfirmationDialogComponent, {
				maxWidth: '392px',
				panelClass: 'pop-up-dialog-smaller-v2',
				data: data,
			});

		dialogRef.afterClosed().subscribe(async (result: any) => {
			if (!!result) {
				if (controlAssignmentIds.length === 1) {
					this.snackbar.open(
						$localize`:@@companyAccessTab-confirmDeletedAccessMsg:Successfully deleted access`,
						undefined,
						{
							duration: 2000,
						}
					);
				} else {
					this.snackbar.open(
						$localize`:@@companyAccessTab-confirmDeletedAccessesMsg:Successfully deleted accesses`,
						undefined,
						{
							duration: 2000,
						}
					);
				}

				this.dataSet.clearAllSelections();
				this.dataSet.applyFilters();
			}
		});
	}

	private updateCheckboxState(): void {
		if (!this.dataSet?.selection || !this.dataSource?.data) {
			this.isAllSelected = false;
			this.isIndeterminate = false;
			return;
		}

		// Get current page data for pagination support
		const currentPageData = this.getCurrentPageData();
		const selectableItems = currentPageData.filter((access) =>
			this.isAccessRecordSelectable(access)
		);

		if (selectableItems.length === 0) {
			this.isAllSelected = false;
			this.isIndeterminate = false;
			return;
		}

		// Count how many selectable items on the current page are selected
		const selectedOnCurrentPage = selectableItems.filter((item) =>
			this.dataSet.selection.isSelected(item)
		).length;

		this.isAllSelected =
			selectedOnCurrentPage > 0 &&
			selectedOnCurrentPage === selectableItems.length;
		this.isIndeterminate =
			selectedOnCurrentPage > 0 &&
			selectedOnCurrentPage < selectableItems.length;
	}

	private getCurrentPageData(): ControlAssignment[] {
		if (!this.dataSource?.data) {
			return [];
		}

		// Check if paginator exists and get current page data
		if (this.dataSource.paginator) {
			const startIndex =
				this.dataSource.paginator.pageIndex *
				this.dataSource.paginator.pageSize;
			const endIndex = startIndex + this.dataSource.paginator.pageSize;
			return this.dataSource.data.slice(startIndex, endIndex);
		}

		// If no paginator, return all data
		return this.dataSource.data;
	}

	isSelectedAllChecked(): boolean {
		return this.isAllSelected;
	}

	isSelectAllIntermediate(): boolean {
		return this.isIndeterminate;
	}

	// Check how many total can be selected/editable on current page
	countTotalSelectableElements() {
		const currentPageData = this.getCurrentPageData();
		let totalSelectableCount = 0;
		currentPageData.forEach((access) => {
			if (this.isAccessRecordSelectable(access)) {
				totalSelectableCount++;
			}
		});
		return totalSelectableCount;
	}

	onSelectAllToggle(checked: boolean): void {
		if (!this.dataSet?.selection) {
			return;
		}

		const currentPageData = this.getCurrentPageData();
		const selectableItems = currentPageData.filter((access) =>
			this.isAccessRecordSelectable(access)
		);

		if (selectableItems.length === 0) {
			return;
		}

		if (checked) {
			// Select only selectable items on the current page
			this.dataSet.selection.select(...selectableItems);
		} else {
			// Deselect only selectable items on the current page
			this.dataSet.selection.deselect(...selectableItems);
		}
	}

	rowId(row: ControlAssignment): string {
		return row?.id || 'unknown-id';
	}

	/**
	 * Determines if an access record should be selectable/editable.
	 * When viewing from a device page (deviceId is set), only allow selection
	 * if the device group name matches the device's serial number (system-generated groups).
	 */
	private isAccessRecordSelectable(access: ControlAssignment): boolean {
		if (!access.canEditAccess) {
			return false;
		}

		// Check if the access is expired, and if so the user shouldn't be allowed to edit that selection
		const currentDate = DateTime.utc();
		const endDate = DateTime.fromISO(
			access.utcEndDate ?? '2500-12-31T23:59:59.999Z',
			{ zone: 'UTC' }
		);
		if (endDate < currentDate) {
			return false;
		}

		// If not viewing from a specific device page, all editable records are selectable
		if (!this.deviceId) {
			return true;
		}

		// Find the specific device that matches the current deviceId
		const currentDevice = this.devices[0];
		if (!currentDevice) {
			return false;
		}

		// Only allow selection if device group name matches device serial number
		// (indicating a system-generated device group, not a user-created one)
		return currentDevice.device.serialNumber === access.deviceGroup?.name;
	}

	// Determines if an access record should be selectable/editable
	isSelectable(access: ControlAssignment): boolean {
		return this.isAccessRecordSelectable(access);
	}
}
